// Hooks Dossiers — EPIC A PR2 (mutations complètes).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SEMI_FRESH } from '../../lib/queryPresets';
import { useAuthStore } from '../../store/useAuthStore';
import {
  editDossierFriseSteps,
  listDossierFrise,
  replaceDossierFrise,
  transitionFriseStep,
} from '../../services/dossiers/friseService';
import {
  type CreateDossierInput,
  type Dossier,
  type DossierFilters,
  type DossierStage,
  type UpdateDossierInput,
  archiveDossier,
  closeDossier,
  createDossier,
  deleteDossier,
  getDossierById,
  listDossierMembers,
  listDossierStages,
  listDossiers,
  listDossiersEnriched,
  reopenDossier,
  setLeadCounsel,
  setStageEffectiveOn,
  transitionStage,
  unarchiveDossier,
  updateDossier,
} from '../../services/dossiers/dossierService';

export const dossiersKeys = {
  all: ['dossiers'] as const,
  list: (filters: DossierFilters = {}) => ['dossiers', 'list', filters] as const,
  detail: (id: string) => ['dossiers', 'detail', id] as const,
  members: (id: string) => ['dossiers', 'members', id] as const,
  stages: (id: string) => ['dossiers', 'stages', id] as const,
  frise: (id: string) => ['dossiers', 'frise', id] as const,
  hearings: (id: string) => ['dossiers', 'hearings', id] as const,
};

export function useDossiers(filters: DossierFilters = {}) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);

  return useQuery({
    ...SEMI_FRESH,
    queryKey: dossiersKeys.list(filters),
    queryFn: () => listDossiers(filters),
    enabled: isInitialized && !!session,
  });
}

export function useDossiersEnriched(filters: Omit<DossierFilters, 'search'> = {}) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: dossiersKeys.list({ ...filters, enriched: true } as DossierFilters),
    queryFn: () => listDossiersEnriched(filters),
  });
}

export function useDossier(id: string | undefined) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: dossiersKeys.detail(id ?? ''),
    queryFn: () => getDossierById(id!),
    enabled: !!id,
  });
}

export function useDossierMembers(id: string | undefined) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: dossiersKeys.members(id ?? ''),
    queryFn: () => listDossierMembers(id!),
    enabled: !!id,
  });
}

export function useCreateDossier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDossierInput) => createDossier(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dossiersKeys.all });
    },
  });
}

export function useUpdateDossier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateDossierInput }) =>
      updateDossier(id, patch),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: dossiersKeys.detail(vars.id) });
      qc.invalidateQueries({ queryKey: dossiersKeys.all });
    },
  });
}

export function useRenameDossier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      updateDossier(id, { title }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: dossiersKeys.detail(vars.id) });
      qc.invalidateQueries({ queryKey: dossiersKeys.all });
    },
  });
}

export function useArchiveDossier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => archiveDossier(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: dossiersKeys.detail(id) });
      qc.invalidateQueries({ queryKey: dossiersKeys.all });
    },
  });
}

export function useUnarchiveDossier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unarchiveDossier(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: dossiersKeys.detail(id) });
      qc.invalidateQueries({ queryKey: dossiersKeys.all });
    },
  });
}

export function useDeleteDossier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteDossier(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: dossiersKeys.detail(id) });
      qc.invalidateQueries({ queryKey: dossiersKeys.all });
    },
  });
}

export function useCloseDossier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, closedAt }: { id: string; closedAt?: string | null }) =>
      closeDossier(id, closedAt),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: dossiersKeys.detail(id) });
      qc.invalidateQueries({ queryKey: dossiersKeys.all });
    },
  });
}

export function useReopenDossier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reopenDossier(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: dossiersKeys.detail(id) });
      qc.invalidateQueries({ queryKey: dossiersKeys.all });
    },
  });
}

export function useSetLeadCounsel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dossierId, collaboratorId }: { dossierId: string; collaboratorId: string }) =>
      setLeadCounsel(dossierId, collaboratorId),
    onSuccess: (_, { dossierId }) => {
      qc.invalidateQueries({ queryKey: dossiersKeys.detail(dossierId) });
      qc.invalidateQueries({ queryKey: dossiersKeys.members(dossierId) });
      qc.invalidateQueries({ queryKey: dossiersKeys.all });
    },
  });
}

// ─── Stage history (A-04) ────────────────────────────────────────────────

export function useDossierStages(id: string | undefined) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: dossiersKeys.stages(id ?? ''),
    queryFn: () => listDossierStages(id!),
    enabled: !!id,
  });
}

function invalidateDossierStage(qc: ReturnType<typeof useQueryClient>, id: string) {
  qc.invalidateQueries({ queryKey: dossiersKeys.detail(id) });
  qc.invalidateQueries({ queryKey: dossiersKeys.stages(id) });
  qc.invalidateQueries({ queryKey: dossiersKeys.frise(id) });
  qc.invalidateQueries({ queryKey: dossiersKeys.all });
}

export function useDossierFrise(id: string | undefined) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: dossiersKeys.frise(id ?? ''),
    queryFn: () => listDossierFrise(id!),
    enabled: !!id,
  });
}

export function useReplaceDossierFrise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      friseId,
      position,
      effectiveOn,
    }: {
      id: string;
      friseId: string;
      position: number;
      effectiveOn: string;
    }) => replaceDossierFrise(id, friseId, position, effectiveOn),
    onSuccess: (_, { id }) => invalidateDossierStage(qc, id),
  });
}

export function useEditDossierFriseSteps() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      steps,
    }: {
      id: string;
      steps: { from: number | null; label: string }[];
    }) => editDossierFriseSteps(id, steps),
    onSuccess: (_, { id }) => invalidateDossierStage(qc, id),
  });
}

export function useTransitionFriseStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      position,
      effectiveOn,
    }: {
      id: string;
      position: number;
      effectiveOn: string;
    }) => transitionFriseStep(id, position, effectiveOn),
    onSuccess: (_, { id }) => invalidateDossierStage(qc, id),
  });
}

export function useTransitionStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      toStage,
      note,
      effectiveOn,
    }: {
      id: string;
      toStage: DossierStage;
      note?: string | null;
      effectiveOn?: string | null;
    }) => transitionStage(id, toStage, note, effectiveOn),
    onSuccess: (_, { id }) => invalidateDossierStage(qc, id),
  });
}

export function useSetStageEffectiveOn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, effectiveOn }: { id: string; effectiveOn: string }) =>
      setStageEffectiveOn(id, effectiveOn),
    onSuccess: (_, { id }) => invalidateDossierStage(qc, id),
  });
}

export type { Dossier };
